import { SendLoginTokenAuth } from '../../../../../redux/fetchActions/tokens';
import cabine from '../../../../../services/cabineApi';
import { CreateReportInput, ReportRequest, TipoRelatorio } from '../interface';

/* eslint-disable camelcase */
interface ApiReportRow {
  id: number;
  id_usuario: number;
  id_parceiro: number;
  nome_parceiro: string;
  solicitado_por: string;
  tipo: 'sintetico' | 'analitico';
  tipo_relatorio?: 'recebimento' | 'tarifas';
  data_inicio: string;
  data_fim: string;
  status: 'queued' | 'processing' | 'ready' | 'error';
  total_linhas: number | null;
  s3_bucket: string | null;
  s3_key: string | null;
  erro_mensagem: string | null;
  processado_em: string | null;
  created_at: string;
  updated_at: string;
}
/* eslint-enable camelcase */

function normalizeDate(value: string | null): string {
  if (!value) return '';
  return value.length >= 10 ? value.slice(0, 10) : value;
}

function mapApi(row: ApiReportRow): ReportRequest {
  return {
    id: String(row.id),
    type: row.tipo,
    // Retrocompat: solicitacoes criadas antes da coluna `tipo_relatorio`
    // existir defaultam para 'recebimento' (mesmo default do banco).
    tipoRelatorio: (row.tipo_relatorio ?? 'recebimento') as TipoRelatorio,
    partnerId: row.id_parceiro,
    partnerName: row.nome_parceiro,
    dateStart: normalizeDate(row.data_inicio),
    dateEnd: normalizeDate(row.data_fim),
    requestedBy: row.solicitado_por,
    status: row.status,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    completedAt: row.processado_em ?? undefined,
    errorMessage: row.erro_mensagem ?? undefined,
  };
}

export class RequestReportError extends Error {
  public statusCode: number;

  public retorno: string;

  public codigoErro?: string;

  constructor(statusCode: number, retorno: string, codigoErro?: string) {
    super(retorno);
    this.name = 'RequestReportError';
    this.statusCode = statusCode;
    this.retorno = retorno;
    this.codigoErro = codigoErro;
  }
}

export async function requestReport(
  input: CreateReportInput
): Promise<ReportRequest> {
  try {
    const response = await cabine.post(
      '/v1/relatorios/tarifacao',
      {
        tipo: input.type,
        tipo_relatorio: input.tipoRelatorio,
        id_parceiro: input.partnerId,
        nome_parceiro: input.partnerName,
        data_inicio: input.dateStart,
        data_fim: input.dateEnd,
        solicitado_por: input.requestedBy,
      },
      SendLoginTokenAuth()
    );
    return mapApi(response.data.dados as ApiReportRow);
  } catch (err: any) {
    const status: number = err?.response?.status ?? 0;
    const retorno: string =
      err?.response?.data?.retorno ?? 'Falha ao criar solicitação.';
    const codigo: string | undefined = err?.response?.data?.erro;
    throw new RequestReportError(status, retorno, codigo);
  }
}

export async function listReports(
  tipoRelatorio?: TipoRelatorio
): Promise<ReportRequest[]> {
  // tipo_relatorio entra como querystring quando informado, para que o
  // backend devolva apenas os relatorios daquela aba (Recebimento ou
  // Tarifas Gerais). Sem o parametro, retorna todos.
  const url = tipoRelatorio
    ? `/v1/relatorios/tarifacao?tipo_relatorio=${tipoRelatorio}`
    : '/v1/relatorios/tarifacao';
  const response = await cabine.get(url, SendLoginTokenAuth());
  const dados = (response.data?.dados as ApiReportRow[]) || [];
  return dados.map(mapApi);
}

export async function getReportById(id: string): Promise<ReportRequest | null> {
  try {
    const response = await cabine.get(
      `/v1/relatorios/tarifacao/${id}`,
      SendLoginTokenAuth()
    );
    return mapApi(response.data.dados as ApiReportRow);
  } catch (err) {
    return null;
  }
}

export async function getDownloadUrl(id: string): Promise<string | null> {
  try {
    const response = await cabine.get(
      `/v1/relatorios/tarifacao/${id}/download`,
      SendLoginTokenAuth()
    );
    return (response.data?.url as string) ?? null;
  } catch (err) {
    return null;
  }
}
